import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, defer, EMPTY, expand, map, Observable, Observer, switchMap, throwError, timeout, TimeoutError, timer } from "rxjs";
import { ApplicationDetails } from "../models/application-details";
import { Candidate } from "../models/candidate";
import { JiraStory } from "../models/jiraStory";
import { Organization } from "../models/organization-field";
import { Project } from "../models/project-field";
import {
  TestReportPayload,
  TestReportSummarization,
} from "../models/test-report-summarization";
import { TestCase, TestCaseVersions } from "../models/testCase";
import { ConfigService } from "./config/config.service";
import { AuthTokenService } from "./keycloak/auth-token.service";
import { TdgService } from "./tdg.service";
import { UtilitiesService } from "./utilities.service";
import { PromptBundleTreeItem, PromptItem, PromptItemVersion, PromptKeyItem, CreatePromptPayload, UpdatePromptVersionPayload, SetActiveVersionPayload } from "../models/prompt-manager";
import { TokenAnalyticsPayload, TokenAnalyticsResponse } from "../models/telemetry-fields";
import {
  TestCoverageAnalysisParams,
  TestCoverageAnalysisResponse,
  TestCoverageResultResponse,
} from "../models/test-coverage";

@Injectable({
  providedIn: "root",
})
export class RestService {
  tenantId = '';

  private localConstants: any = {
    TESTCASE_TO_MAPPING: "TestCase to Source Code Mappings",
    CODE_COVERAGE: "Code Coverage",
    GET_RECOMMENDATIONS: "Get Recommendations",
  };

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private utilityService: UtilitiesService,
    private tdgService: TdgService,
    private authToken: AuthTokenService
  ) {}

  get baseUrl(): string {
    return this.configService.getConfig("apiUrl");
  }

  private get tdgUrl(): string {
    return this.configService.getConfig("tdgUrl");
  }

  private get telemetryUrl(): string {
    return this.configService.getConfig("telemetryUrl");
  }

  private get agenticUrl(): string {
    return this.configService.getConfig("agenticUrl");
  }

  getTenantId() {
    const isDomainResolver = this.configService.getConfig("isDomainResolver");
    // return isDomainResolver
    //   ? (this.utilityService?.extractRealmFromUrl() as string)
    //   : (sessionStorage.getItem("selectedRealm") as string) || 'master';
    return (this.utilityService?.extractRealmFromUrl() as string) || 'master'
  }

  // Set common headers
  private getHeaders(): HttpHeaders {
    const isDomainResolver = this.configService.getConfig("isDomainResolver");
    return new HttpHeaders({
      "X-TenantId": this.getTenantId()
    });
  }

  // Utility for making GET requests
  private get<T>(url: string, params?: any): Observable<T> {
    return this.http
      .get<T>(url, { headers: this.getHeaders(), params })
      .pipe(catchError(this.handleError));
  }

  // Utility for making POST requests
  private post<T>(url: string, body: any): Observable<T> {
    return this.http
      .post<T>(url, body, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }

  // Utility for making POST requests with params in url
  private postP<T>(url: string, params: any): Observable<T> {
    const httpParams = new HttpParams({ fromObject: params });
    return this.http
      .post<T>(`${url}?${httpParams.toString()}`, null, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  // Utility for making PATCH requests
  private patch<T>(url: string, body: any): Observable<T> {
    return this.http
      .patch<T>(url, body, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }

  // Utility for making PUT requests
  private put<T>(url: string, body: any): Observable<T> {
    return this.http
      .put<T>(url, body, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }

  // Delete Request
  private delete<T>(url: string, params?: any): Observable<T> {
    return this.http
      .delete<T>(url, { headers: this.getHeaders(), body: params })
      .pipe(catchError(this.handleError));
  }

  // Multipart FormData POST request
  postFormData<T>(url: string, formData: FormData): Observable<T> {
    return this.http
      .post<T>(url, formData, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }

  // Multipart FormData PUT request
  putFormData<T>(url: string, formData: FormData): Observable<T> {
    return this.http
      .put<T>(url, formData, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }

  // Error handling
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = "Something went wrong; please try again later.";
    if (error.error instanceof ErrorEvent) {
      errorMessage = `An error occurred: ${error.error.message}`;
    } else {
      errorMessage = error?.error?.message ? error?.error?.message : error?.error?.detail;
    }
    return throwError(() => new Error(errorMessage));
  }

  // Fetch organization details
  getOrganization(): Observable<Organization[]> {
    return this.get<Organization[]>(`${this.baseUrl}/organization/public`);
  }

  // Fetch the currently logged-in user's profile
  getLoggedUser(): Observable<any> {
    return this.get<any>(`${this.baseUrl}/user/logged-user`);
  }

  // Post token analytics
  postTokenAnalytics(payload: TokenAnalyticsPayload): Observable<TokenAnalyticsResponse> {
    return this.post<TokenAnalyticsResponse>(`${this.telemetryUrl}/telemetry/token-analytics`, payload);
  }

  // Create a new organization
  createOrganization(organization: Organization): Observable<Organization> {
    return this.post<Organization>(
      `${this.baseUrl}/organization`,
      organization
    );
  }

  // Update organization details
  updateOrganization(organization: Organization): Observable<Organization> {
    var data = {
      owner: organization.owner,
      name: organization.name,
      description: organization.description,
      mappingKey: organization.mappingKey,
      logo: organization.logo,
      isDomainRestricted: organization.isDomainRestricted,
      restrictedDomains: organization.restrictedDomains,
      isIRSAEnabled: organization.isIRSAEnabled ?? false,
      isAlmConfigured: organization.isAlmConfigured ?? false,
    };
    return this.patch<Organization>(
      `${this.baseUrl}/organization/${organization.id}`,
      data
    );
  }

  // Delete an organization
  deleteOrganization(orgId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}/organization/${orgId}`, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  /** GET /project/{projectId} */
  getProjectById(projectId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/project/${projectId}`);
  }

  // Fetch project details
  getProject(): Observable<Project[]> {
    return this.http
      .get<Project[]>(`${this.baseUrl}/project`, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  // Create a new organization
  createProject(project: Project): Observable<Project> {
    return this.http
      .post<Project>(`${this.baseUrl}/project`, project, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  // Update project details
  updateProject(project: Project, projectId: Project): Observable<Project> {
    return this.http
      .patch<Project>(`${this.baseUrl}/project/${projectId}`, project, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  // Delete an organization
  deleteProject(projectId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}/project/${projectId}`, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  // Fetch full organization details by mapping key (includes owner email and all fields)
  getOrganizationByMappingKey(mappingKey: string): Observable<any> {
    return this.http
      .get<any>(`${this.baseUrl}/organization/mapping/${mappingKey}`)
      .pipe(catchError(this.handleError));
  }

  /** Current tenant settings. GET /organization/config */
  getOrganizationConfig(): Observable<any> {
    return this.get<any>(`${this.baseUrl}/organization/config`);
  }

  /** Admin / owner org-wide sync. POST /project/sync */
  syncTenantProjects(): Observable<any> {
    return this.post<any>(`${this.baseUrl}/project/sync`, {});
  }

  /** Project user sync of accessible JIRA projects. POST /project/sync/access */
  syncAccessibleTenantProjects(): Observable<any> {
    return this.post<any>(`${this.baseUrl}/project/sync/access`, {});
  }

  // check mapping key
  checkMappingKey(mappingKey: string): Observable<boolean> {
    return this.http
      .get<{ isValid: boolean }>(
        `${this.baseUrl}/organization/valid-mapping/${mappingKey}`
      )
      .pipe(map((response: any) => !response.data))
      .pipe(catchError(this.handleError));
  }

  //validate token
  validateToken(token: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/onboarding/validate/${token}`);
  }

  getProjectAssignCandidatesDetails(
    projectId: string
  ): Observable<Candidate[]> {
    return this.get<Candidate[]>(
      `${this.baseUrl}/project/${projectId}/users/org`
    );
  }

  assignCandidatesForProject(
    projectId: string,
    payload: Candidate[]
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/project/${projectId}/users/org`,
      payload
    );
  }

  inviteCandidate(projectId: string, data: Candidate): Observable<Candidate> {
    return this.post<any>(`${this.baseUrl}/project/${projectId}/invite`, data);
  }

  onboardingUser(payload: Candidate): Observable<Candidate> {
    return this.post<Candidate>(`${this.baseUrl}/onboarding/signup`, payload);
  }

  createConnection(id: any, data: any) {
    return this.post<any>(`${this.baseUrl}/settings/source/${id}`, data);
  }

  /** Org-wide ALM (or other) source — no project id. POST /settings/tenant/source */
  createTenantSource(data: any) {
    return this.post<any>(`${this.baseUrl}/settings/tenant/source`, data);
  }

  /** Current tenant ALM/source connection. GET /settings/tenant/source */
  getTenantSource(): Observable<any> {
    return this.get<any>(`${this.baseUrl}/settings/tenant/source`);
  }

  getConnections(projectId: any) {
    return this.get<any>(
      `${this.baseUrl}/settings/source/${projectId}/type/map`
    );
  }

  // ── BPD Document / Knowledge Graph ──────────────────────────────────────

  /**
   * Upload a new BPD document file (multipart).
   * POST /settings/file/{projectId}
   * Body: type=local, name=<documentName>, file=<file>
   * Returns the created file connection in the response.
   */
  uploadNewTcgDocument(projectId: string, file: File, documentType: string, documentName: string): Observable<any> {
    const formData = new FormData();
    formData.append('type', 'local');
    formData.append('name', documentName);
    formData.append('file', file, file.name);
    return this.postFormData<any>(`${this.baseUrl}/settings/file/${projectId}`, formData);
  }

  /**
   * Fetch column names for the import field-mapping step.
   * POST /tcg/testcase/import/preview-columns
   * Zephyr also sends folderIds for fully selected folders and TestCaseKeys for individually selected cases.
   * Returns { systemTestCasesFields: [{ fieldKey, label, required }], extractedColumns: [{ source, field }] }
   */
  getImportPreviewColumns(
    projectId: string,
    connectionId: string,
    selection?: { folderIds: string[]; TestCaseKeys: string[] },
  ): Observable<any> {
    return this.post<any>(`${this.baseUrl}/tcg/testcase/import/preview-columns`, {
      projectId,
      connectionId,
      ...(selection
        ? { folderIds: selection.folderIds, testCaseKeys: selection.TestCaseKeys }
        : {}),
    });
  }

  /**
   * Fetch previously saved column mappings for a connection.
   * GET /tcg/project/{projectId}/connection/{connectionId}/testcase/import/mapping
   * Returns { columnMappings: Record<string, { source, field } | string> } or null when none saved yet.
   */
  getImportMapping(projectId: string, connectionId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/tcg/project/${projectId}/connection/${connectionId}/testcase/import/mapping`
    );
  }

  /**
   * Flat Zephyr Essential folder list for a project connection.
   * GET /tcg/project/{projectId}/connection/{connectionId}/zephyr/folders
   * Returns { data: { values: [{ id, parentId, name, index }] } }.
   */
  getZephyrFolders(projectId: string, connectionId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/tcg/project/${projectId}/connection/${connectionId}/zephyr/folders`
    );
  }

  /**
   * Zephyr test cases for one folder. folderId is optional; startAt pages the result.
   * GET /tcg/project/{projectId}/connection/{connectionId}/zephyr/testcases
   */
  getZephyrTestCases(
    projectId: string,
    connectionId: string,
    folderId?: string,
    startAt = 0
  ): Observable<any> {
    const params: { folderId?: string; startAt?: number } = {};
    if (folderId) {
      params.folderId = folderId;
    }
    if (startAt > 0) {
      params.startAt = startAt;
    }
    return this.get<any>(
      `${this.baseUrl}/tcg/project/${projectId}/connection/${connectionId}/zephyr/testcases`,
      params
    );
  }

  /**
   * Save field mappings and retrieve extracted test cases.
   * POST /tcg/testcase/import/save_mapping
   * Zephyr also sends folderIds and testCaseKeys so the backend can extract those cases.
   * @param columnMappings fieldKey → { source, field } from preview-columns.
   * Returns { columnMappings, extractedTestCases: [...] }
   */
  saveImportMapping(
    projectId: string,
    connectionId: string,
    columnMappings: Record<string, { source: string; field: string }>,
    selection?: { folderIds: string[]; TestCaseKeys: string[] },
  ): Observable<any> {
    return this.post<any>(`${this.baseUrl}/tcg/testcase/import/save_mapping`, {
      projectId,
      connectionId,
      columnMappings,
      ...(selection
        ? { folderIds: selection.folderIds, testCaseKeys: selection.TestCaseKeys }
        : {}),
    });
  }

  /** Step 1 – register the uploaded file with the BPD pipeline. */
  uploadBpdDocument(projectId: string, fileConnection: string): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/tcg/file/bpd/upload`,
      { projectId, fileConnection }
    );
  }

  /** Step 2 – kick off Knowledge Graph generation from the BPD document. */
  generateBpdKnowledgeGraph(projectId: string, fileConnection: string): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/tcg/file/bpd/knowledgegraph`,
      { projectId, fileConnection }
    );
  }

  /** Step 3 – poll KG progress (call every 30 s). */
  getBpdKgProgress(projectId: string, connectionId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/tcg/file/bpd/knowledgegraph/project/${projectId}/connection/${connectionId}`
    );
  }

  /** Step 4 – trigger test-case generation.
   *  testCaseType: functional_smoke | functional_sanity | functional_regression | non_functional
   */
  generateBpdTestCases(projectId: string, fileConnection: string, testCaseType: string): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/tcg/file/bpd/testcases`,
      { projectId, fileConnection, testCaseType }
    );
  }

  /** Step 5 – fetch the generated test cases. */
  getBpdTestCases(projectId: string, connectionId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/tcg/file/bpd/testcases/project/${projectId}/connection/${connectionId}`
    );
  }

  updateConnection(projectId: any, connId: any, data: any) {
    return this.http.patch<any>(
      `${this.baseUrl}/settings/source/${projectId}/${connId}`,
      data,
      { headers: this.getHeaders() }
    );
  }

  deleteConnection(projectId: string, connId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}/settings/source/${projectId}/${connId}`, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Pre-delete validation for a storage (FILE) connection.
   * Returns the list of scripts that reference this file.
   * An empty / null data array means the file is safe to delete.
   */
  validateDeleteStorageConnection(
    projectId: string,
    connId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/settings/source/${projectId}/${connId}/valid-delete`
    );
  }

  createFileConnection(id: any, data: any) {
    return this.post<any>(`${this.baseUrl}/settings/file/${id}`, data);
  }

  getReportSummarization(
    payload: TestReportPayload
  ): Observable<TestReportSummarization[]> {
    return this.post<TestReportSummarization[]>(
      `${this.baseUrl}/metrics/get-data`,
      payload
    );
  }

  uploadReportFile(projectId: string, payload: any) {
    return this.post<any>(
      `${this.baseUrl}/trs/${projectId}/upload-file`,
      payload
    );
  }

  getMannualUploadedFiles(
    projectId: string,
    releaseId: string
  ): Observable<ApplicationDetails[]> {
    return this.get<ApplicationDetails[]>(
      `${this.baseUrl}/trs/${projectId}/get-uploaded-files`,
      {
        "sprint-id": releaseId,
      }
    );
  }

  rollBackFile(projectId: string, releaseId: string, fileName: string) {
    return this.postP<any>(
      `${this.baseUrl}/trs/${projectId}/roll-back-upload`,
      {
        "sprint-id": releaseId,
        "file-name": fileName,
      }
    );
  }

  getApplicationDetails(projectId: string): Observable<ApplicationDetails[]> {
    return this.get<ApplicationDetails[]>(
      `${this.baseUrl}/project/${projectId}/applications`
    );
  }

  updateApplicationsDetails(
    projectId: string,
    payload: ApplicationDetails[]
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/project/${projectId}/applications`,
      payload
    );
  }

  getSideNavApplicationDetails(projectId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/common/navbar/project/${projectId}`);
  }

  getJiraUserStories(
    projectId: string,
    jiraConnection: string
  ): Observable<JiraStory> {
    return this.post<JiraStory>(`${this.baseUrl}/tcg/source/extractStories`, {
      projectId,
      jiraConnection,
    });
  }

  getStorageUserStories(
    projectId: string,
    fileConnection: string
  ): Observable<JiraStory> {
    return this.post<JiraStory>(`${this.baseUrl}/tcg/file/extractStories`, {
      projectId,
      fileConnection,
    });
  }

  getKgNodes(projectId: string, configId: string, payload: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/tcg/getKnowledgeGraphNodes/${projectId}/${configId}?limit=2000`,
      payload
    );
  }

  getUnifiedKgNodes(projectId: string, configId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/tcg/getUnifiedKnowledgeGraphNodes/${projectId}/${configId}?limit=2000`
    );
  }

  downloadKnowledgeGraph(projectId: string, configId: string, payload: any): Observable<any> {
    return this.post<any>(`${this.baseUrl}/tcg/getKnowledgeGraphHTML/${projectId}/${configId}`, payload);
  }

  downloadUnifiedKnowledgeGraph(projectId: string, configId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/tcg/getUnifiedKnowledgeGraphHTML/${projectId}/${configId}`);
  }

  deleteJiraStories(projectId: string): Observable<any> {
    return this.delete<any>(`${this.baseUrl}/tcg/jira/${projectId}`);
  }

  generateTestCase(selectedStories: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/tcg/jira/generateTestcases`,
      selectedStories
    );
  }

  updateTestCase(payload: any): Observable<any> {
    return this.post<any>(`${this.baseUrl}/tcg/testcase/update`, payload);
  }

  editTestCase(projectId: any, versionId: any, payload: any): Observable<any> {
    return this.put<any>(
      `${this.baseUrl}/tcg/testcaseresult/edit/${projectId}/${versionId}`,
      payload
    );
  }

  persistTestCase(projectId: any, payload: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/tcg/testcase/persist/${projectId}`,
      payload
    );
  }

  deleteTestCase(
    projectId: any,
    versionId: any,
    payload: any[]
  ): Observable<any> {
    return this.http.delete<any>(
      `${this.baseUrl}/tcg/testcaseresult/delete/${projectId}/${versionId}`,
      { body: payload, headers: this.getHeaders() }
    );
  }

  getTestCaseVersions(projectId: string): Observable<TestCaseVersions> {
    return this.get<TestCaseVersions>(
      `${this.baseUrl}/tcg/testcase/${projectId}`
    );
  }

  /**
   * Create an empty test suite.
   * POST /testsuite/project/{projectId}
   * Returns the created suite (including its id).
   */
  createTcgTestSuite(projectId: string, testSuiteName: string): Observable<any> {
    return this.post<any>(`${this.baseUrl}/testsuite/project/${projectId}`, { testSuiteName });
  }

  /**
   * Persist imported test cases into an existing test suite.
   * POST /testsuite/project/{projectId}/testsuite/{testSuiteId}/testcases
   * @param origin Possible values: EXCEL | CSV | ZEPHYR_ESSENTIAL | MANUAL | TCG_AI
   */
  addImportedTestCasesToTestSuite(
    projectId: string,
    testSuiteId: string,
    origin: string,
    connectionId: string,
    extractedTestCases: any[]
  ): Observable<any> {
    const mappedTestCases = (extractedTestCases ?? []).map((tc: any) => {
      const { userStoryId, ...rest } = tc ?? {};
      return {
        ...rest,
        key: rest.key || userStoryId || rest.storyId || "",
      };
    });
    return this.post<any>(
      `${this.baseUrl}/testsuite/project/${projectId}/testsuite/${testSuiteId}/imported-testcases`,
      { origin, connectionId, extractedTestCases: mappedTestCases }
    );
  }

  getTestCaseByVersionId(
    projectId: string,
    versionId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/tcg/testcase/${projectId}/${versionId}`
    );
  }

  /**
   * Cancel test case generation for a specific version
   * DELETE: /backend/api/v1/tcg/cancelTestcases/{projectId}/{versionId}
   * @param projectId - Project ID
   * @param versionId - Version ID of the test cases to cancel
   * @returns Observable with cancellation response
   */
  cancelTestcaseGeneration(projectId: string, versionId: string): Observable<any> {
    return this.delete<any>(
      `${this.baseUrl}/tcg/cancelTestcases/${projectId}/${versionId}`
    );
  }

  downloadExcel(projectId: string, data: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/tcg/${projectId}/exportToExcel`,
      data
    );
  }

  getTestCases(projectId: string): Observable<TestCase[]> {
    return this.get<TestCase[]>(
      `${this.baseUrl}/testPrioritizationAndSelection/mappings/${projectId}/testcases`
    );
  }

  getTestCasesWithId(projectId: string, token: string): Observable<TestCase[]> {
    return this.get<TestCase[]>(
      `${this.baseUrl}/testPrioritizationAndSelection/mappings/${projectId}/testcases/${token}`
    );
  }

  createMapping(payload: any): Observable<any> {
    return this.postFormData<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/createMapping`,
      payload
    );
  }

  updateMapping(payload: any): Observable<any> {
    return this.putFormData<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/mappings`,
      payload
    );
  }

  validateGit(formData: any): Observable<any> {
    return this.postFormData<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/repository/validate`,
      formData
    );
  }

  createAnalyze(id: string): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/mappings/${id}/analyze`,
      { id }
    );
  }

  createCoverage(id: string): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/calculateCoverage`,
      { id }
    );
  }

  createRecommendations(id: string): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/generateRecommendations`,
      { id }
    );
  }

  createCodeSnipped(id: string): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/mappings/${id}/code_snippet`,
      {}
    );
  }

  testCaseFileUpload(mappingId: string, formData: any): Observable<any> {
    return this.postFormData<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/mappings/${mappingId}/testcase_document`,
      formData
    );
  }

  /**
   * Common service handler to execute REST calls dynamically
   */
  executeServiceCall(name: string, id: string): Observable<any> {
    switch (name) {
      case this.localConstants.TESTCASE_TO_MAPPING:
        return this.createAnalyze(id);
      case this.localConstants.CODE_COVERAGE:
        return this.createCoverage(id);
      case this.localConstants.GET_RECOMMENDATIONS:
        return this.createRecommendations(id);
      default:
        return throwError(() => new Error("Invalid action name"));
    }
  }

  approveMapping(payload: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/mapping/approve`,
      payload
    );
  }

  getRecomendationDetails(mappingId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/mappings/${mappingId}/recommendations`
    );
  }

  getImpactfulTestCases(projectId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/projects/${projectId}/mappings`
    );
  }

  getTestCaseCoverage(mappingId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/mappings/${mappingId}/coverage`
    );
  }

  deleteMapping(mappingId: string): Observable<any> {
    return this.delete<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/mappings/${mappingId}`
    );
  }

  deleteMappingsBulk(mappingIds: any): Observable<any> {
    return this.delete<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/mappings/delete-multiple`,
      {
        ids: mappingIds,
      }
    );
  }

  getAllApplicationsInRoot(organization: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/application/${organization}`);
  }

  manageApplicationOrgLevel(
    organization: string,
    applicationList: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/application/${organization}/manage`,
      applicationList
    );
  }

  generatedImpactedTestcases(mappingId: string, url: string): Observable<any> {
    // generatedImpactedTestcases(mode: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/impacted-testcases`,
      { id: mappingId, githubLink: url }
    );
  }

  uploadDefectReport(mappingId: string, formData: any): Observable<any> {
    return this.postFormData<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/mappings/${mappingId}/uploadDefectReport`,
      formData
    );
  }

  getGeneratedImpactedTestcases(mappingId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/mappings/${mappingId}/impacted-testcases`
    );
  }

  getMostImpactedTestcases(
    mappingId: string,
    reportName: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/mappings/${mappingId}/most-impacted-testcases`,
      { defectReport: reportName }
    );
  }

  deleteDefectReport(mappingId: string): Observable<any> {
    return this.delete<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/mappings/${mappingId}/delete-defect-report`
    );
  }

  getSurveySubmission(projectId: string): Observable<any> {
    return this.post<any>(`${this.baseUrl}/surveySubmission/get`, {
      projectId,
      surveyName: "projectOnboardingSurvey",
    });
  }

  surveySubmission(payload: any): Observable<any> {
    return this.post<any>(`${this.baseUrl}/surveySubmission/submit`, payload);
  }

  getExecutions(projectId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/execution/${projectId}`);
  }

  getExecutionSummaryData(
    projectId: string,
    executionId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/execution/${projectId}/${executionId}`
    );
  }

  handleAsyncRequest(id: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/common/asyncRequest/${id}`);
  }

  getDownloadUrl(fileKey: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/common/s3/download?key=${fileKey}`);
  }

  createTestSuit(projectId: string, payload: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/asg/${projectId}/testSuite`,
      payload
    );
  }

  lockScript(scriptId: string): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/script/${scriptId}/lock`,
      {}
    );
  }

  unlockScript(scriptId: string): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/script/${scriptId}/unlock`,
      {}
    );
  }

  createScript(
    projectId: string,
    automationProjectId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script`,
      payload
    );
  }

  updateScript(
    projectId: string,
    automationProjectId: string,
    scriptId: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${scriptId}`,
      payload
    );
  }

  renameScript(
    projectId: string,
    automationProjectId: string,
    scriptId: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${scriptId}/rename`,
      payload
    );
  }

  duplicateScript( projectId: string, automationProjectId: string, scriptId: string, payload:any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${scriptId}/duplicate`,payload
    );
  }

  createStep(
    projectId: string,
    automationProjectId: string,
    scriptId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${scriptId}/step`,
      payload
    );
  }

  getScriptById(
    projectId: string,
    automationProjectId: string,
    scriptId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${scriptId}`
    );
  }

  getRecordedActivities(
    projectId: string,
    automationProjectId: string,
    scriptId: string,
    payload: { token: string }
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${scriptId}/loadRecordedScript`,
      payload
    );
  }

  saveRecordedActivities(
    projectId: string,
    automationProjectId: string,
    scriptId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${scriptId}/saveRecordedScript`,
      payload
    );
  }

  // Variables
  createVariable(
    automationProjectId: string,
    scriptId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${automationProjectId}/script/${scriptId}/variable`,
      payload
    );
  }

  updateVariable(
    scriptId: string,
    variableId: string,
    payload: any
  ): Observable<any> {
    return this.put<any>(
      `${this.baseUrl}/automation/${scriptId}/variable/${variableId}`,
      payload
    );
  }

  deleteVariable(scriptId: string, variableId: string): Observable<any> {
    return this.delete<any>(
      `${this.baseUrl}/automation/${scriptId}/variable/${variableId}`
    );
  }

  getVariableById(scriptId: string, variableId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${scriptId}/variable/${variableId}`
    );
  }

  getVariablesByScript(scriptId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/automation/${scriptId}/variables`);
  }

  // Global Variables
  getGlobalVariables(
    projectId: string,
    automationProjectId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/globalVariables`
    );
  }

  createGlobalVariable(
    projectId: string,
    automationProjectId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/globalVariable`,
      payload
    );
  }

  updateGlobalVariable(
    projectId: string,
    automationProjectId: string,
    variableId: string,
    payload: any
  ): Observable<any> {
    return this.put<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/globalVariable/${variableId}`,
      payload
    );
  }

  deleteGlobalVariable(
    projectId: string,
    automationProjectId: string,
    variableId: string
  ): Observable<any> {
    return this.delete<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/globalVariable/${variableId}`
    );
  }


  getDatasources(
    projectId: string,
    automationProjectId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/dataSource`
    );
  }

  getIntellisense(
    projectId: string,
    automationProjectId: string,
    scriptId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${scriptId}/intellisense`
    );
  }

  // Parameters
  createParameter(
    automationProjectId: string,
    scriptId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${automationProjectId}/script/${scriptId}/parameter`,
      payload
    );
  }

  updateParameter(
    scriptId: string,
    paramId: string,
    payload: any
  ): Observable<any> {
    return this.put<any>(
      `${this.baseUrl}/automation/${scriptId}/parameter/${paramId}`,
      payload
    );
  }

  deleteParameter(scriptId: string, paramId: string): Observable<any> {
    return this.delete<any>(
      `${this.baseUrl}/automation/${scriptId}/parameter/${paramId}`
    );
  }

  getParametersByScript(scriptId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/automation/${scriptId}/parameters`);
  }

  getTestSuits(projectId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/asg/${projectId}/testSuite`);
  }

  getTestScripts(projectId: string, testSuitId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/asg/${projectId}/testSuite/${testSuitId}/testScript`
    );
  }

  createInstance(projectId: string): Observable<any> {
    return this.post<any>(`${this.baseUrl}/ideManager/createInstance`, {
      projectId,
    });
  }

  deleteInstance(projectId: string): Observable<any> {
    return this.post<any>(`${this.baseUrl}/ideManager/deleteRunningInstance`, {
      projectId,
    });
  }

  deleteTestSuit(projectId: string, testSuitId: string): Observable<any> {
    return this.delete<any>(
      `${this.baseUrl}/asg/${projectId}/testSuite/${testSuitId}`
    );
  }

  deleteTestScript(
    projectId: string,
    testSuitId: any,
    testScriptId: string
  ): Observable<any> {
    return this.delete<any>(
      `${this.baseUrl}/asg/${projectId}/testSuite/${testSuitId}/testScript/${testScriptId}`
    );
  }

  updateTestSuit(
    projectId: string,
    testSuitId: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/asg/${projectId}/testSuite/${testSuitId}`,
      payload
    );
  }

  updateTestScript(
    projectId: string,
    testSuitId: string,
    testScriptId: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/asg/${projectId}/testSuite/${testSuitId}/testScript/${testScriptId}`,
      payload
    );
  }

  getTestSuitSignedUrl(): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/common/s3/download/key?fileKey=orgbackend/49e7490e-fb2b-46cd-8fd9-7dbe2a86c0f2/uploads/04477d70-417b-4c75-9666-22be46c4ac2c_Framework.zip`
    );
  }

  getExecutionSignedUrl(url: any): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/common/s3/download/key?fileKey=${url}`
    );
  }

  downloadExtention(projectId: string): Observable<any> {
    return this.post<any>(`${this.baseUrl}/asg/buildExtension`, {
      projectId,
    });
  }

  createAutomationProject(projectId: string, payload: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject`,
      payload
    );
  }

  updateAutomationProject(
    projectId: string,
    automationId: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationId}`,
      payload
    );
  }

  deteleAutomationProject(projectId: string, id: string): Observable<void> {
    return this.delete<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${id}`
    );
  }

  getAutomationProject(projectId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject`
    );
  }

  getAutomationProjectDetails(
    projectId: string,
    automationProjectId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}`
    );
  }

  // Automation - application
  getAutomationApplications(
    projectId: string,
    automationProjectId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application`
    );
  }

  addAutomationApplications(
    projectId: string,
    automationProjectId: string,
    payload: any
  ): Observable<any> {
    return this.postFormData<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application`,
      payload
    );
  }

  getAutomationApplicationsById(
    projectId: string,
    automationProjectId: string,
    id: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${id}`
    );
  }

  deleteAutomationApplicationsById(
    projectId: string,
    automationProjectId: string,
    id: string
  ): Observable<void> {
    return this.delete<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${id}`
    );
  }

  updateAutomationApplicationsById(
    projectId: string,
    automationProjectId: string,
    id: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${id}`,
      payload
    );
  }

  deleteAutomationApplications(
    projectId: string,
    automationProjectId: string,
    idList: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/delete`,
      idList
    );
  }

  // Automation - Test Suits
  getAutomationTestSuits(
    projectId: string,
    automationProjectId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/scriptGroup`
    );
  }

  addAutomationTestSuits(
    projectId: string,
    automationProjectId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/scriptGroup`,
      payload
    );
  }

  deleteAutomationTestSuitsById(
    projectId: string,
    automationProjectId: string,
    id: string
  ): Observable<void> {
    return this.delete<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/scriptGroup/${id}`
    );
  }

  updateAutomationTestSuitsById(
    projectId: string,
    automationProjectId: string,
    id: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/scriptGroup/${id}`,
      payload
    );
  }

  // Automation - Test Script
  getAutomationTestScripts(
    projectId: string,
    automationProjectId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script`
    );
  }

  getAutomationTestScriptsByScriptGroups(
    projectId: string,
    automationProjectId: string,
    scriptGroupIds: any
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/scriptGroup/${scriptGroupIds}`
    );
  }

  addAutomationTestScript(
    projectId: string,
    automationProjectId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script`,
      payload
    );
  }

  deleteValidationApp(
    projectId: string,
    automationProjectId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/valid-delete`,
      payload
    );
  }

  deleteValidationPage(
    projectId: string,
    automationProjectId: string,
    automationApplicationId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${automationApplicationId}/page/valid-delete`,
      payload
    );
  }

  deleteValidationUiObject(
    projectId: string,
    automationProjectId: string,
    automationApplicationId: string,
    pageId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${automationApplicationId}/page/${pageId}/uiObject/valid-delete`,
      payload
    );
  }

  deleteValidationReusableScript(
    projectId: string,
    automationProjectId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/scripts/valid-delete`,
      payload
    );
  }

  deleteAutomationTestScriptById(
    projectId: string,
    automationProjectId: string,
    id: string
  ): Observable<void> {
    return this.delete<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${id}`
    );
  }

  bulkDeleteAutomationTestScripts(
    projectId: string,
    automationProjectId: string,
    idList: any
  ): Observable<void> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/scripts/delete`,
      idList
    );
  }

  bulkDeleteAutomationTestSuites(
    projectId: string,
    automationProjectId: string,
    idList: any
  ): Observable<void> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/suites/delete`,
      idList
    );
  }

  updateAutomationTestScriptById(
    projectId: string,
    automationProjectId: string,
    id: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${id}`,
      payload
    );
  }

  mapToTestCase(
    projectId: string,
    automationProjectId: string,
    id: string,
    mappedId: string
  ) {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${id}/mapToTestCase`,
      { tcgTestCaseResultId: mappedId }
    );
  }

  getTestCasesFromTCG(projectId: any) {
    return this.get<any>(`${this.baseUrl}/tcg/testcaseToScript/${projectId}`);
  }

  getTestCaseById(projectId: string, testCaseId: string) {
    return this.get<any>(
      `${this.baseUrl}/tcg/getTestCaseById/${projectId}/${testCaseId}`
    );
  }

  addMissingTestCase(projectId: string, payload: any) {
    return this.post<any>(
      `${this.baseUrl}/tcg/testcase/persist/${projectId}`,
      payload
    );
  }

  // Automation - Page
  getAutomationPage(
    projectId: string,
    automationProjectId: string,
    automationApplicationId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${automationApplicationId}/page`
    );
  }

  addAutomationPage(
    projectId: string,
    automationProjectId: string,
    automationApplicationId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${automationApplicationId}/page`,
      payload
    );
  }

  getAutomationPageById(
    projectId: string,
    automationProjectId: string,
    automationApplicationId: string,
    id: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${automationApplicationId}/page/${id}`
    );
  }

  deleteAutomationPageById(
    projectId: string,
    automationProjectId: string,
    automationApplicationId: string,
    id: string
  ): Observable<void> {
    return this.delete<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${automationApplicationId}/page/${id}`
    );
  }

  deleteAutomationPages(
    projectId: string,
    automationProjectId: string,
    automationApplicationId: string,
    pagesList: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${automationApplicationId}/page/delete`,
      pagesList
    );
  }

  updateAutomationPageById(
    projectId: string,
    automationProjectId: string,
    automationApplicationId: string,
    id: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${automationApplicationId}/page/${id}`,
      payload
    );
  }

  // Automation - UI Object
  getAutomationUiObject(
    projectId: string,
    automationProjectId: string,
    automationApplicationId: string,
    pageId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${automationApplicationId}/page/${pageId}/uiObject`
    );
  }

  addAutomationUiObject(
    projectId: string,
    automationProjectId: string,
    automationApplicationId: string,
    pageId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${automationApplicationId}/page/${pageId}/uiObject`,
      payload
    );
  }

  getAutomationUiObjectById(
    projectId: string,
    automationProjectId: string,
    automationApplicationId: string,
    pageId: string,
    id: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${automationApplicationId}/page/${pageId}/uiObject/${id}`
    );
  }

  deleteAutomationUiObjectById(
    projectId: string,
    automationProjectId: string,
    automationApplicationId: string,
    pageId: string,
    id: string
  ): Observable<void> {
    return this.delete<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${automationApplicationId}/page/${pageId}/uiObject/${id}`
    );
  }

  deleteAutomationUiObjects(
    projectId: string,
    automationProjectId: string,
    automationApplicationId: string,
    pageId: string,
    objectList: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${automationApplicationId}/page/${pageId}/uiObject/delete`,
      objectList
    );
  }

  updateAutomationUiObjectById(
    projectId: string,
    automationProjectId: string,
    automationApplicationId: string,
    pageId: string,
    id: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/application/${automationApplicationId}/page/${pageId}/uiObject/${id}`,
      payload
    );
  }

  // get Command Definition
  getCommandDefinitions(): Observable<any> {
    return this.get<any>(`${this.baseUrl}/automation/commandDefinitions`);
  }

  //getAgents
  getAgents(projectId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/automation/${projectId}/agent `);
  }

  //get Agent by id
  getAgentById(projectId: string, agentId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/agent/${agentId} `
    );
  }

  //update agent
  updateAgent(
    projectId: string,
    agentId: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/agent/${agentId}`,
      payload
    );
  }

  //create agent
  createAgent(projectId: string, payload: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/agent`,
      payload
    );
  }

  deleteAgent(projectId: string, agents: []) {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/agent/delete`,
      agents
    );
  }

  //reclaim agent
  reclaimAgent(projectId: string, agents: []): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/agent/reClaimAgent`,
      agents
    );
  }

  runAgent(
    projectId: string,
    automationProjectId: string,
    scriptId: string,
    payload: { agentId: string; agentType: string }
  ) {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${scriptId}/buildAndRun`,
      payload
    );
  }

  getExecutionStack(
    projectId: string,
    automationProjectId: string,
    scriptId: string
  ) {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${scriptId}/buildAndRunExecutions`
    );
  }

  getTestSuitsAndTestCases(
    projectId: string,
    automationProjectId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/withVersions `
    );
  }

  getAgentsWithStatus(projectId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/automation/${projectId}/agent`);
  }

  addNewTestPlan(
    projectId: string,
    automationProjectId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/testPlan`,
      payload
    );
  }

  getTestPlans(
    projectId: string,
    automationProjectId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/testPlan `
    );
  }

  getTestPlansWithVersions(
    projectId: string,
    automationProjectId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/testPlan/deep `
    );
  }

  getTestPlanById(
    projectId: string,
    automationProjectId: string,
    planId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/testPlan/${planId} `
    );
  }

  EditTestPlanById(
    projectId: string,
    automationProjectId: string,
    planId: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/testPlan/${planId} `,
      payload
    );
  }

  deletTestPlansById(
    projectId: string,
    automationProjectId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/testPlan/delete`,
      payload
    );
  }

  startExecution(projectId: string, automationProjectId: string, payload: any) {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/executionInstances/startExecution`,
      payload
    );
  }

  getTestExecutionSummary(
    projectId: string,
    automationProjectId: string,
    payload: any
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/automationExecution`, payload
    );
  }

  getAutomationExecution(
    projectId: string,
    automationProjectId: string,
    executionId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/automationExecution/${executionId}`
    );
  }

  getRecorderExtention(): Observable<any> {
    return this.get<any>(`${this.baseUrl}/common/s3/download/extension`);
  }

  getAgentExtention(projectId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/agent/downloadLocalAgent`
    );
  }

  downloadExecutionReport(
    projectId: string,
    automationProjectId: string,
    id: string,
    type: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/report/${id}?type=${type}`
    );
  }

  downloadAutomationProject(
    projectId: string,
    automationProjectId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/downloadBundle`
    );
  }

  /**
   * GET /automation/{projectId}/automationProject/{automationProjectId}/export
   * Returns the zip bytes. includeSecrets defaults to false and omits passwords.
   */
  exportAutomationProject(
    projectId: string,
    automationProjectId: string,
    includeSecrets = false
  ): Observable<any> {
    return this.http
      .get(
        `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/export`,
        {
          headers: this.getHeaders(),
          params: { includeSecrets },
          responseType: "blob",
          observe: "response",
        }
      )
      .pipe(catchError((error: HttpErrorResponse) => this.readApiError(error)));
  }

  /**
   * POST /automation/{projectId}/automationProject/import
   * Multipart file is the export zip. name is optional.
   */
  importAutomationProject(projectId: string, file: File, name?: string): Observable<any> {
    const formData = new FormData();
    formData.append("file", file, file.name);
    if (name?.trim()) {
      formData.append("name", name.trim());
    }
    return this.http
      .post<any>(
        `${this.baseUrl}/automation/${projectId}/automationProject/import`,
        formData,
        { headers: this.getHeaders() }
      )
      .pipe(catchError((error: HttpErrorResponse) => this.readApiError(error)));
  }

  private readApiError(error: HttpErrorResponse): Observable<never> {
    const fail = (body: any) =>
      throwError(() => ({
        status: error.status,
        message: body?.message || error.message || "Something went wrong; please try again later.",
        errors: Array.isArray(body?.errors) ? body.errors : [],
      }));
    if (error.error instanceof Blob) {
      return new Observable((observer) => {
        error.error.text().then((text: string) => {
          let body: any = {};
          try {
            body = text ? JSON.parse(text) : {};
          } catch {
            body = { message: text };
          }
          fail(body).subscribe({ error: (parsed) => observer.error(parsed) });
        });
      });
    }
    return fail(error.error);
  }

  loadReleases(projectId: any) {
    return this.get<any>(`${this.baseUrl}/release/${projectId}`);
  }

  createRelease(projectId: any, release: any) {
    return this.post<any>(`${this.baseUrl}/release/${projectId}`, release);
  }

  updateRelease(projectId: any, releaseId: any, release: any) {
    return this.patch<any>(
      `${this.baseUrl}/release/${projectId}/${releaseId}`,
      release
    );
  }

  removeReleases(projectId: any, releaseIdList: []) {
    return this.delete<any>(`${this.baseUrl}/release/${projectId}`, {
      ids: releaseIdList,
    });
  }

  getReleaseById(projectId: string, releaseId: string) {
    return this.get<any>(`${this.baseUrl}/release/${projectId}/${releaseId}`);
  }

  getSampleReportDownloadLink(projectId: string) {
    return this.get<any>(
      `${this.baseUrl}/trs/${projectId}/download-sample-template`
    );
  }

  syncJira(projectId: string) {
    return this.get<any>(`${this.baseUrl}/trs/${projectId}/syncJira`);
  }

  saveSelfhealedObjects(
    projectId: string,
    automationProjectId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/uiObject/saveSelfHealObjects`,
      payload
    );
  }
  addNewSchedule(projectId: string, automationProjectId: string, payload: any) {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/testScheduler`,
      payload
    );
  }

  updateScheduleById(
    projectId: string,
    automationProjectId: string,
    id: string,
    payload: any
  ) {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/testScheduler/${id}`,
      payload
    );
  }

  mapToDataSource(
    projectId: string,
    automationProjectId: string,
    scriptId: string,
    dataSourceId: string
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${scriptId}/mapToDataSource`,
      { automationDataSourceId: dataSourceId }
    );
  }

  getDataSourceById(
    projectId: string,
    automationProjectId: string,
    dataSourceId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/dataSource/${dataSourceId}/view`
    );
  }

  getAllSchedules(projectId: string, automationProjectId: string) {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/testScheduler`
    );
  }

  enableDisableSchedule(
    projectId: string,
    automationProjectId: string,
    id: string,
    status: boolean
  ) {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/testScheduler/${id}/enable`,
      {
        enable: status,
      }
    );
  }

  getPlansByScheduleId(
    projectId: string,
    automationProjectId: string,
    id: string
  ) {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/testScheduler/${id}`
    );
  }

  getReleases(projectId: string) {
    return this.get<any>(`${this.baseUrl}/release/${projectId}`);
  }

  saveSchedule(projectId: string, automationProjectId: string, payload: any) {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/testScheduler`,
      payload
    );
  }

  deleteSchedule(projectId: string, automationProjectId: string, payload: any) {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/testScheduler/delete`,
      payload
    );
  }

  testConnection(projectId: string, jiraConnectionId: string) {
    return this.get<any>(
      `${this.baseUrl}/settings/source/${projectId}/validALMConn/${jiraConnectionId}`
    );
  }

  testLLMConnection(projectId: string, llmConnectionId: string) {
    return this.get<any>(
      `${this.baseUrl}/settings/source/${projectId}/validLLMConn/${llmConnectionId}`
    );
  }

   testParserConnection(projectId: string, parserConnectionId: string) {
    return this.get<any>(
      `${this.baseUrl}/settings/source/${projectId}/validParserConn/${parserConnectionId}`
    );
  }

  testDbConnection(projectId: string, dbConnectionId: string) {
    return this.get<any>(
      `${this.baseUrl}/settings/source/${projectId}/validDBConn/${dbConnectionId}`
    );
  }

  generateTestCasesByLLM(projectId: string, payload: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/tsg/projects/${projectId}/versions`,
      payload
    );
  }

  triggerJobByLLM(projectId: string, versionId: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/tsg/projects/${projectId}/versions/${versionId}/run-job`,
      {}
    );
  }

  getLLMStatus(projectId: string, versionId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/tsg/projects/${projectId}/versions/${versionId}/status`
    );
  }

  getLLMGeneratedTestScripts(projectId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/tsg/projects/${projectId}/versions`);
  }

  getLLMGeneratedTestScriptsByVersions(
    projectId: string,
    versionId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/tsg/projects/${projectId}/versions/${versionId}`
    );
  }

  generateScriptsByLLM(requestObj: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/agentic-llm-tsg/generate-scripts`,
      requestObj
    );
  }

  getAIRecordedActivitiesStatus(
    projectId: string,
    llmTsgId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/agentic-llm-tsg/projects/${projectId}/agentic-llm-tsg/${llmTsgId}`
    );
  }

  /**
   * Live recording for a manual agentic script run.
   * GET /agentic-llm-tsg/projects/{projectId}/agentic-llm-tsg/{id}/video
   * The body is multipart/x-mixed-replace; boundary=frame. Each emission is one
   * JPEG. The connection stays open until the recording ends or `signal` aborts.
   */
  streamAgenticLlmTsgVideo(
    projectId: string,
    id: string,
    signal: AbortSignal
  ): Observable<Blob> {
    return new Observable<Blob>((subscriber) => {
      const abort = new AbortController();
      const onAbort = () => abort.abort();
      if (signal.aborted) {
        abort.abort();
      } else {
        signal.addEventListener("abort", onAbort);
      }
      let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
      this.readAgenticLlmTsgVideo(projectId, id, abort.signal, subscriber, (next) => {
        reader = next;
      })
        .catch((error: unknown) => {
          if (abort.signal.aborted || (error as { name?: string })?.name === "AbortError") {
            subscriber.complete();
            return;
          }
          subscriber.error(error);
        });
      return () => {
        signal.removeEventListener("abort", onAbort);
        abort.abort();
        reader?.cancel().catch(() => undefined);
      };
    });
  }

  private async readAgenticLlmTsgVideo(
    projectId: string,
    id: string,
    signal: AbortSignal,
    subscriber: Observer<Blob>,
    setReader: (reader: ReadableStreamDefaultReader<Uint8Array>) => void
  ): Promise<void> {
    const token = await this.authToken.getAccessToken();
    if (signal.aborted) {
      subscriber.complete();
      return;
    }
    const response = await fetch(
      `${this.baseUrl}/agentic-llm-tsg/projects/${projectId}/agentic-llm-tsg/${id}/video`,
      {
        method: "GET",
        headers: {
          "X-TenantId": this.getTenantId(),
          Authorization: `Bearer ${token}`,
        },
        signal,
      }
    );
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      const error = new Error(body || `Recording stream failed (${response.status})`) as Error & { status?: number };
      error.status = response.status;
      if (response.status === 400 && this.isRecordingInactive(body)) {
        error.name = "RecordingInactiveError";
      }
      throw error;
    }
    if (!response.body) {
      throw new Error("Recording stream failed (empty body)");
    }
    const boundary = this.mixedReplaceBoundary(response.headers.get("Content-Type"));
    const delimiter = new TextEncoder().encode(`--${boundary}`);
    const reader = response.body.getReader();
    setReader(reader);
    let buffer = new Uint8Array(0);
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      if (!value?.byteLength) {
        continue;
      }
      buffer = this.appendBytes(buffer, value);
      buffer = this.emitRecordingFrames(buffer, delimiter, (frame) => subscriber.next(frame));
    }
    subscriber.complete();
  }

  private isRecordingInactive(body: string): boolean {
    return body.includes("The manual recording is not active.")
      || body.includes("The manual recording has not started.");
  }

  private mixedReplaceBoundary(contentType: string | null): string {
    const match = /boundary=(?:"([^"]+)"|([^;\s]+))/i.exec(contentType || "");
    return (match?.[1] || match?.[2] || "frame").trim();
  }

  private emitRecordingFrames(
    buffer: Uint8Array,
    delimiter: Uint8Array,
    onFrame: (frame: Blob) => void
  ): Uint8Array {
    let offset = 0;
    while (offset < buffer.length) {
      const start = this.indexOfBytes(buffer, delimiter, offset);
      if (start < 0) {
        const keepFrom = Math.max(offset, buffer.length - Math.max(delimiter.length - 1, 0));
        return buffer.slice(keepFrom);
      }
      if (this.isClosingBoundary(buffer, start, delimiter.length)) {
        return new Uint8Array(0);
      }
      const headersAt = start + delimiter.length;
      const headerEnd = this.headerBreakAt(buffer, headersAt);
      if (!headerEnd) {
        if (buffer.length > start + 512) {
          offset = start + delimiter.length;
          continue;
        }
        return buffer.slice(start);
      }
      const header = new TextDecoder().decode(buffer.slice(headersAt, headerEnd.index));
      const lengthMatch = /content-length:\s*(\d+)/i.exec(header);
      const bodyStart = headerEnd.index + headerEnd.length;
      if (lengthMatch) {
        const length = Number(lengthMatch[1]);
        if (buffer.length < bodyStart + length) {
          return buffer.slice(start);
        }
        const frame = this.jpegBlob(buffer.slice(bodyStart, bodyStart + length));
        if (frame) {
          onFrame(frame);
        }
        offset = bodyStart + length;
        continue;
      }
      const next = this.indexOfBytes(buffer, delimiter, bodyStart);
      if (next < 0) {
        return buffer.slice(start);
      }
      const frame = this.jpegBlob(this.withoutTrailingCrlf(buffer.slice(bodyStart, next)));
      if (frame) {
        onFrame(frame);
      }
      offset = next;
    }
    return new Uint8Array(0);
  }

  private headerBreakAt(buffer: Uint8Array, from: number): { index: number; length: number } | null {
    const windowEnd = Math.min(buffer.length, from + 512);
    const headers = buffer.subarray(0, windowEnd);
    const crlf = this.indexOfBytes(headers, new TextEncoder().encode("\r\n\r\n"), from);
    const lf = this.indexOfBytes(headers, new TextEncoder().encode("\n\n"), from);
    if (crlf >= 0 && (lf < 0 || crlf <= lf)) {
      return { index: crlf, length: 4 };
    }
    if (lf >= 0) {
      return { index: lf, length: 2 };
    }
    return null;
  }

  private isClosingBoundary(buffer: Uint8Array, start: number, delimiterLength: number): boolean {
    return buffer[start + delimiterLength] === 45 && buffer[start + delimiterLength + 1] === 45;
  }

  private withoutTrailingCrlf(bytes: Uint8Array): Uint8Array {
    if (bytes.length >= 2 && bytes[bytes.length - 2] === 13 && bytes[bytes.length - 1] === 10) {
      return bytes.slice(0, bytes.length - 2);
    }
    if (bytes.length >= 1 && bytes[bytes.length - 1] === 10) {
      return bytes.slice(0, bytes.length - 1);
    }
    return bytes;
  }

  private jpegBlob(body: Uint8Array): Blob | null {
    if (body.length < 4 || body[0] !== 0xff || body[1] !== 0xd8) {
      return null;
    }
    const copy = new ArrayBuffer(body.byteLength);
    new Uint8Array(copy).set(body);
    return new Blob([copy], { type: "image/jpeg" });
  }

  private appendBytes(left: Uint8Array, right: Uint8Array): Uint8Array {
    const combined = new Uint8Array(left.length + right.length);
    combined.set(left, 0);
    combined.set(right, left.length);
    return combined;
  }

  private indexOfBytes(haystack: Uint8Array, needle: Uint8Array, from: number): number {
    if (!needle.length || from < 0) {
      return -1;
    }
    const last = haystack.length - needle.length;
    for (let index = from; index <= last; index++) {
      let matched = true;
      for (let offset = 0; offset < needle.length; offset++) {
        if (haystack[index + offset] !== needle[offset]) {
          matched = false;
          break;
        }
      }
      if (matched) {
        return index;
      }
    }
    return -1;
  }

  /**
   * Stop a manual agentic script run.
   * POST /agentic-llm-tsg/projects/{projectId}/agentic-llm-tsg/{id}/cancel
   */
  cancelAgenticLlmTsg(projectId: string, id: string): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/agentic-llm-tsg/projects/${projectId}/agentic-llm-tsg/${id}/cancel`,
      {}
    );
  }

  getFrameworkList(projectId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/framework-types`
    );
  }

  updateFrameworkType(
    projectId: string,
    automationProjectId: string,
    payload: any
  ): Observable<any> {
    return this.put<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/framework-type`,
      payload
    );
  }

  getLLMProviderFields(type: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/settings/sourceConDef`, {
      type: type,
    });
  }

  configureLLM(id: any, data: any) {
    return this.post<any>(
      `${this.baseUrl}/settings/source/${id}/configureLLM`,
      data
    );
  }

  getLLMConfs(id: any) {
    return this.get<any>(`${this.baseUrl}/settings/source/${id}/configureLLM`);
  }

  configureLLaMa(id: any, data: any) {
    return this.post<any>(
      `${this.baseUrl}/settings/source/${id}/configureLLMParser`,
      data
    );
  }

  getLLaMaConfs(id: any) {
    return this.get<any>(
      `${this.baseUrl}/settings/source/${id}/configureLLMParser`
    );
  }

  APIScriptGenerate(id: any, data: any) {
    return this.post<any>(
      `${this.baseUrl}/llm-api-script-generator/scripts`,
      data
    );
  }

  addRestAPIService(
    projectId: string,
    automationProjectId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service`,
      payload
    );
  }

  updateRestAPIService(
    projectId: string,
    automationProjectId: string,
    serviceId: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}`,
      payload
    );
  }

  getServiceById(
    projectId: string,
    automationProjectId: string,
    serviceId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}`
    );
  }

  getAllServices(
    projectId: string,
    automationProjectId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service`
    );
  }

  deleteServices(
    projectId: string,
    automationProjectId: string,
    idList: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/delete`,
      idList
    );
  }

  getServiceOperationById(
    projectId: string,
    automationProjectId: string,
    serviceId: string,
    operationId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}/operation/${operationId}`
    );
  }

  getAllServiceOperations(
    projectId: string,
    automationProjectId: string,
    serviceId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}/operation`
    );
  }

  deleteServiceOperations(
    projectId: string,
    automationProjectId: string,
    serviceId: string,
    idList: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}/operation/delete`,
      idList
    );
  }

  addServiceOperation(
    projectId: string,
    automationProjectId: string,
    serviceId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}/operation`,
      payload
    );
  }

  updateServiceOperation(
    projectId: string,
    automationProjectId: string,
    serviceId: string,
    operationId: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}/operation/${operationId}`,
      payload
    );
  }

  getParameters(
    projectId: string,
    automationProjectId: string,
    serviceId: string,
    operationId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}/operation/${operationId}/parameter`
    );
  }

  deleteParameters(
    projectId: string,
    automationProjectId: string,
    serviceId: string,
    operationId: string,
    idList: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}/operation/${operationId}/parameter/delete`,
      idList
    );
  }

  addParameter(
    projectId: string,
    automationProjectId: string,
    serviceId: string,
    operationId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}/operation/${operationId}/parameter`,
      payload
    );
  }

  updateOperationParameters(
    projectId: string,
    automationProjectId: string,
    serviceId: string,
    operationId: string,
    parameterId: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}/operation/${operationId}/parameter/${parameterId}`,
      payload
    );
  }

  getAllHeaders(
    projectId: string,
    automationProjectId: string,
    serviceId: string,
    operationId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}/operation/${operationId}/header`
    );
  }

  deleteHeaders(
    projectId: string,
    automationProjectId: string,
    serviceId: string,
    operationId: string,
    idList: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}/operation/${operationId}/header/delete`,
      idList
    );
  }

  addHeader(
    projectId: string,
    automationProjectId: string,
    serviceId: string,
    operationId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}/operation/${operationId}/header`,
      payload
    );
  }

  updateHeader(
    projectId: string,
    automationProjectId: string,
    serviceId: string,
    operationId: string,
    headerId: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}/operation/${operationId}/header/${headerId}`,
      payload
    );
  }

  addReturnType(
    projectId: string,
    automationProjectId: string,
    serviceId: string,
    operationId: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}/operationData/${operationId}`,
      payload
    );
  }

  getApiLLMScriptStatus(projectId: string, llmApiId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/llm-api-script-generator/projects/${projectId}/llm-api-scripts/${llmApiId}`
    );
  }

  saveApiLLMValidation(projectId: string, llmApiId: string, payload: any) {
    return this.post<any>(
      `${this.baseUrl}/llm-api-script-generator/projects/${projectId}/llm-api-scripts/${llmApiId}/services`,
      payload
    );
  }

  uploadAppContext(projectId: string, payload: any) {
    return this.post<any>(
      `${this.baseUrl}/settings/file/${projectId}`,
      payload
    );
  }

  extractUserStoriesALM(payload: any) {
    return this.post<any>(
      `${this.baseUrl}/tcg/source/extractStories/`,
      payload
    );
  }

  processApplicationContext(
    projectId: string,
    fileConnection: string
  ): Observable<any> {
    return this.post<any>(`${this.baseUrl}/tcg/file/applicationContext`, {
      projectId,
      fileConnection,
    });
  }

  handleAsyncApplicationContext(id: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/tcg/applicationContext/${id}`);
  }

  getSampleUserStoryFileDownload(projectId: string, type: any) {
    return this.get<any>(
      `${this.baseUrl}/tcg/${projectId}/download-sample-template`,
      { type: type }
    );
  }

  deleteApplicationContext(projectId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}/tcg/applicationContext/${projectId}`, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  getAutomationSchema(
    projectId: string,
    automationProjectId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/schema`
    );
  }

  deleteValidationOperation(
    projectId: string,
    automationProjectId: string,
    serviceId: string,
    payload: any
  ): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/service/${serviceId}/operation/valid-delete`,
      payload
    );
  }

  addDevice(projectId: string, payload: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/device`,
      payload
    );
  }

  editDevice(
    projectId: string,
    deviceId: string,
    payload: any
  ): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/device/${deviceId}`,
      payload
    );
  }

  getDevices(projectId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/automation/${projectId}/device`);
  }

  deleteDevices(projectId: string, payload: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/device/delete`,
      payload
    );
  }

  createMobileTask(projectId: string, payload: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/llm-mobile-script-generator/projects/${projectId}/create-task`,
      payload
    );
  }

  getMobileTaskDetail(projectId: string, taskId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/llm-mobile-script-generator/projects/${projectId}/create-task/${taskId}?detail=true`);
  }

  getUserStoryProgress(
    projectId: string,
    configId: any,
    page = 0,
    size = 10,
    fromTimestamp?: string | null,
  ): Observable<any> {
    let url =
      `${this.baseUrl}/tcg/${projectId}/config/${configId}/progress?page=${page}&size=${size}`;
    if (fromTimestamp) {
      url += `&fromTimestamp=${encodeURIComponent(fromTimestamp)}`;
    }
    return this.get<any>(url);
  }

  getALMExtractedStories(projectId: string, configId: any, filters: any): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/tcg/${projectId}/alm-extractor/userstories/config/${configId}?status=${filters.status}&release=&page=${filters.page}&size=${filters.size}&sortBy=rciScore&direction=desc`
    );
  }

  getAlmStatusList(projectId: string, configId: any): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/tcg/${projectId}/config/${configId}/metadata/statuses`
    );
  }

  getAlmConfigByConnection(projectId: string, connectionId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/tcg/${projectId}/connections/${connectionId}/config`
    );
  }

  getExecutionLogs(projectId: string, automationProjectId: string, taskInstanceId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/executionTaskInstances/${taskInstanceId}/logs`);
  }

  getRciReport(projectId: string, configId: string, reportId: string) {
    return this.get<any>(`${this.baseUrl}/tcg/${projectId}/alm-extractor/userstory/config/${configId}/report/${reportId}`);
  }

  getTDGSchemaList(project_id: string): Observable<any> {
    const formData = new FormData();
    formData.append('tenant', this.getTenantId());
    formData.append('project_id', project_id);

    return this.http.post<any>(`${this.tdgUrl}/getFileList`, formData, {
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    });
  }

  uploadSchemaFiles(project_id: string, files: []): Observable<any> {
    const formData = new FormData();

    files.forEach((file: any) => {
      formData.append('file', file);
    });
    formData.append('tenant', this.getTenantId());
    formData.append('project_id', project_id);

    return this.http.post<any>(`${this.tdgUrl}/uploadFile`, formData, {
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    });
  }

  sendPromptTDG(project_id: string, fileName: string, userInput: string, sessionId?: string,parseFileId?: string, testCaseId?: string): Observable<any> {
    const formData = new FormData();
    formData.append('tenant', this.getTenantId());
    formData.append('project_id', project_id);
    formData.append('fileName', fileName);
    formData.append('user_input', userInput);
    if(sessionId) formData.append('session_id', sessionId);
    if(parseFileId) formData.append('parseFileId', parseFileId); 
    if(testCaseId) formData.append('testCaseId', testCaseId);


    return this.http.post<any>(`${this.tdgUrl}/chat`, formData, {
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    });
  }

  checkStatusTDG(project_id: string, taskId: string): Observable<any> {
    const formData = new FormData();
    formData.append('tenant', this.getTenantId());
    formData.append('project_id', project_id);
    formData.append('task_id', taskId);

    return this.http.post<any>(`${this.tdgUrl}/check-status`, formData, {
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    }).pipe(
      timeout(20000)
    );
  }

  /**
   * Poll TDG check-status sequentially: wait for each response before the next
   * call. A call that does not return within 20s, or that fails with a gateway
   * timeout, is dropped and sent again so a stuck completed poll does not leave
   * the UI waiting.
   */
  pollTdgTaskStatus(
    projectId: string,
    taskId: string,
    pollIntervalMs = 5000
  ): Observable<any> {
    const stillRunning = (status: string) =>
      ['processing', 'pending', 'in_progress', 'running'].includes(
        (status || '').toLowerCase()
      );

    const shouldRetry = (err: any) =>
      err instanceof TimeoutError ||
      (err instanceof HttpErrorResponse &&
        (err.status === 0 || err.status === 502 || err.status === 504));

    const pollOnce = (): Observable<any> =>
      this.checkStatusTDG(projectId, taskId).pipe(
        catchError((err: any) =>
          shouldRetry(err)
            ? timer(pollIntervalMs).pipe(switchMap(() => pollOnce()))
            : throwError(() => err)
        )
      );

    return defer(pollOnce).pipe(
      expand((res: any) =>
        stillRunning(res?.status)
          ? timer(pollIntervalMs).pipe(switchMap(() => pollOnce()))
          : EMPTY
      )
    );
  }

  parseSqlTDG(payload:any): Observable<any> {
    payload.tenant = this.getTenantId();
    return this.http.post<any>(`${this.tdgUrl}/parse_file`, payload, {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'X-Tenantid': this.getTenantId()
      }),
    });
  }

  saveCustomData(projectId: string, tables: string[]): Observable<any> {
    let payload = {
      CustomTable: {
        projectId,
        tables
      }
    };

    return this.http.post<any>(`${this.tdgUrl}/custom-table`, payload, {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'X-Tenantid': this.getTenantId()
      }),
    });
  }

  getCustomTableList(projectId: string) {
    return this.http.get<any>(`${this.tdgUrl}/custom-tables-list/${projectId}`, {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'X-TenantId': this.getTenantId()
      })
    });
  }

  getCustomTablesByTestSuite(projectId: string, testSuite: any) {
    return this.http.get<any>(`${this.tdgUrl}/custom-tables-by-testsuite/${projectId}/${testSuite}`, {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'X-TenantId': this.getTenantId()
      })
    });
  }

  getCustomTableById(projectId: string, tId: string) {
    return this.http.get<any>(`${this.tdgUrl}/custom-tables/${projectId}/${tId}`, {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'X-TenantId': this.getTenantId()
      })
    });
  }

  syncCustomTables(parseFileId: string, projectId: string) {
    const payload = {
      parseFileId: parseFileId,
      project_id: projectId
    };
    return this.http.post<any>(`${this.tdgUrl}/sync-custom-tables`, payload, {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-TenantId': this.getTenantId()
      })
    });
  }
  //persist-data-batch
  persistDataBatch(ids: string, projectId: string, automationProjectId: string) {
    const payload = {
      ids: ids,
      project_id: projectId,
      automationProjectId: automationProjectId
    };
    return this.http.post<any>(`${this.tdgUrl}/persist-data-batch`, payload, {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-TenantId': this.getTenantId()
      })
    });
  }

  updateCustomTable(projectId: string, tableId: string, tableData: any) {
    const payload = {
      tableData: tableData
    };
    return this.http.put<any>(`${this.tdgUrl}/custom-tables/${projectId}/${tableId}`, payload, {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-TenantId': this.getTenantId()
      })
    });
  }

  updateCustomTableData(payload: any) {
    return this.http.put<any>(`${this.tdgUrl}/custom-table-update`, payload, {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-TenantId': this.getTenantId()
      })
    });
  }

  persistTestCaseData(payload: { id: string; automationProjectId: string; project_id: string }) {
    return this.http.post<any>(`${this.tdgUrl}/persist-data`, payload, {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-TenantId': this.getTenantId()
      })
    });
  }

  private persistDataUrl: string = '';
  persistData(connectionName: string, file: File, attributes: any[], datasource: string, projectId: string, automationProjectId: string): Observable<Blob> {
    this.persistDataUrl = `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/dataConnection/tdg`;
    return new Observable((observer: any) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', connectionName);
      formData.append('attributes', JSON.stringify(attributes));
      if (datasource) formData.append('data_source', datasource);
      this.http.post(this.persistDataUrl, formData, {
        headers: new HttpHeaders({
          Accept: 'application/json',
          'X-TenantId': this.getTenantId(),
        }),
        responseType: 'json',
        reportProgress: true,
        observe: 'body',
      }).subscribe(
        (response: any) => {
          observer.next(response);
          observer.complete();
        },
        (error: any) => {
          observer.error(error);
        }
      );
    });
  }

  persistCustomTable(payload: any) {
    return this.http.post<any>(`${this.tdgUrl}/persist-custom-table`, payload, {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'X-TenantId': this.getTenantId()
      })
    });
  }

  updateCustomData(payload: any): Observable<any> {
    return this.http.put<any>(
      `${this.tdgUrl}/custom-table-update`, payload, {
        headers: new HttpHeaders({ 'Accept': 'application/json', 'X-TenantId': this.getTenantId() })
    }
    );
  }

  downloadExcelTDG(data: any): Observable<Blob> {
    let payload: any = {
      tables: data
    };
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<Blob>(`${this.tdgUrl}/downloadExcel`, payload, {
      headers,
      responseType: 'blob' as 'json'
    });
  }

  customTableDelete(projectId:string , tableId:any, dataSource: string, automationProjectId: string){
    let payload: any = {
      projectId: projectId,
      tableIds : [tableId],
      automation_project: automationProjectId,
      dataSource: dataSource
    };
    return this.http.delete(`${this.tdgUrl}/custom-tables-delete`, {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'X-TenantId': this.getTenantId()
      }),
      body: payload 
    });
    
  }

  deleteTestData(payload:any){
    return this.http.delete(`${this.tdgUrl}/delete-test-data`, {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'X-TenantId': this.getTenantId()
      }),
      body: payload 
    });
    
  }
  
  deleteExecutionsList(projectId: string,automationProjectId: string, executionIdList:any){
    return this.post<any>(`${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/automationExecution/delete`,executionIdList);
  }

  aiApproach(projectId: string, approach: string): Observable<any> {
    return this.post<any>(`${this.baseUrl}/project/${projectId}/aiApproach`, { 'aiApproach': approach });
  }

  getSelectedAiApproach(projectId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/project/${projectId}/aiApproach`);
  }

  getAllChats(projectId: string): Observable<any> {
    return this.get<any>(`${this.agenticUrl}/chats`, { project_id: projectId });
  }

  getChatHistory(projectId: string, chatId: string): Observable<any> {
    return this.get<any>(`${this.agenticUrl}/history/${chatId}`, { project_id: projectId});
  }

  updateChatTitle(chatId: any,projectId: string, payload: any): Observable<any> {
    return this.put<any>(`${this.agenticUrl}/chats/${chatId}/title?project_id=${projectId}`, payload);
  }

  getSampleChats(projectId: string): Observable<any> {
    return this.get<any>(`${this.agenticUrl}/sample-chat-titles`, { project_id: projectId });
  }

  getSampleChatHistory(projectId: string, chatId: string): Observable<any> {
    return this.get<any>(`${this.agenticUrl}/sample-history/${chatId}`, { project_id: projectId});
  }

  /**
   * Saves the test plan execution cache data (selected engine, browser, version per script)
   * to the agentic orchestrator so the configuration can be reused on next execution.
   */
  saveTestPlanExecutionCache(payload: {
    project_id: string;
    thread_id: string;
    save_type: string;
    test_plans: any[];
  }): Observable<any> {
    return this.post<any>(`${this.agenticUrl}/test-plan-execution-cache-data`, payload);
  }

  /**
   * Saves the execution meta-data (self-healing, release, test-failure-analysis toggles)
   * to the agentic orchestrator before the execution chat prompt is sent.
   */
  saveExecutionMetaData(payload: {
    project_id: string;
    thread_id: string;
    save_type: 'MetaData';
    meta_data: {
      selfHealing: string;
      releaseId: string;
      testFailureAnalysis: string;
    };
  }): Observable<any> {
    return this.post<any>(`${this.agenticUrl}/test-plan-execution-cache-data`, payload);
  }

  /**
   * Saves an Ad-Hoc execution cache entry.
   * Only the rows the user checked (row.select === true) inside each section
   * are included in the adhoc_plans testScripts array.
   */
  saveAdHocExecutionCache(payload: {
    project_id: string;
    thread_id: string;
    save_type: 'AdHoc';
    adhoc_plans: any[];
  }): Observable<any> {
    return this.post<any>(`${this.agenticUrl}/test-plan-execution-cache-data`, payload);
  }

  getHighImpactTestCaseProgress(projectId: string, versionId: string): Observable<any> {
     return this.get<any>(
      `${this.baseUrl}/tcg/prioritytestcase/${projectId}/${versionId}`
    );
  }

  startAnalysis(params: TestCoverageAnalysisParams): Observable<TestCoverageAnalysisResponse> {
    return this.http.post<TestCoverageAnalysisResponse>(
      `${this.baseUrl}/testCoverage/analyses`,
      params,
      { headers: this.getHeaders() }
    );
  }

  getResults(
    params: Pick<TestCoverageAnalysisParams, 'project_id' | 'version_id' | 'config_id'>
  ): Observable<TestCoverageResultResponse> {
    return this.http.post<TestCoverageResultResponse>(
      `${this.baseUrl}/testCoverage/results`,
      params,
      { headers: this.getHeaders() }
    );
  }

  getEditorStatus(projectId: string, automationProjectId: string, scriptId: string) {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${scriptId}/editor/status`
    );
  }

  setEditorOpenStatus(projectId: string, automationProjectId: string, scriptId: string) {
    return this.http.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${scriptId}/editor/open`,
      {},
      { headers: this.getHeaders() }
    );
  }

  syncEditor(projectId: string, automationProjectId: string, scriptId: string) {
    return this.http.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${scriptId}/editor/sync`,
      {},
      { headers: this.getHeaders() }
    );
  }

  closeEditor(projectId: string, automationProjectId: string, scriptId: string) {
    return this.http.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/script/${scriptId}/editor/close`,
      {},
      { headers: this.getHeaders() }
    );
  }

  getMappingDetailsById(mappingId: string) {
    return this.get<any>(
      `${this.baseUrl}/testPrioritizationAndSelection/mappings/${mappingId}/testcases`
    );
  }

  // ── Pipeline Config endpoints ──────────────────────────────────────────────

  getPipelineConfigs(projectId: string, automationProjectId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/pipelineConfigs`
    );
  }

  getPipelineConfigById(projectId: string, automationProjectId: string, id: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/pipelineConfigs/${id}`
    );
  }

  savePipelineConfig(projectId: string, automationProjectId: string, payload: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/pipelineConfigs`,
      payload
    );
  }

  updatePipelineConfig(projectId: string, automationProjectId: string, id: string, payload: any): Observable<any> {
    return this.patch<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/pipelineConfigs/${id}`,
      payload
    );
  }

  deletePipelineConfig(projectId: string, automationProjectId: string, id: string): Observable<any> {
    return this.delete<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/pipelineConfigs/${id}`
    );
  }

  getPipelinePlatforms(projectId: string, automationProjectId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/pipelineConfigs/ciScript/platforms`
    );
  }

  getPipelineCiScript(projectId: string, automationProjectId: string, platform: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/pipelineConfigs/ciScript`,
      { params: { platform } }
    );
  }

  getCiScriptByConfigId(projectId: string, automationProjectId: string, pipelineConfigId: string, platform: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/pipelineConfigs/${pipelineConfigId}/ciScript`,
      { platform }
    );
  }

  executePipelineConfig(projectId: string, automationProjectId: string, id: string): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/automation/${projectId}/automationProject/${automationProjectId}/pipelineConfigs/${id}/execute`,
      {}
    );
  }

  // ── Prompt Manager ──────────────────────────────────────────────────────────

  private get promptManagerUrl(): string {
    return this.configService.getConfig('promptManagerUrl') ?? '';
  }

  /** Returns the full service / provider / model bundle tree. */
  getPromptBundleTree(): Observable<PromptBundleTreeItem[]> {
    return this.http
      .get<PromptBundleTreeItem[]>(`${this.promptManagerUrl}/ui/bundles/tree`)
      .pipe(catchError(this.handleError));
  }

  /** Returns all prompts belonging to the given bundle. */
  getPromptsByBundle(bundleId: string): Observable<PromptItem[]> {
    return this.http
      .get<PromptItem[]>(`${this.promptManagerUrl}/ui/bundles/${bundleId}/prompts`)
      .pipe(catchError(this.handleError));
  }

  /** Returns all versions for a prompt. */
  getPromptVersions(promptId: string): Observable<any> {
    return this.http
      .get<any>(`${this.promptManagerUrl}/ui/prompts/${promptId}/versions`)
      .pipe(catchError(this.handleError));
  }

  /** Delete a prompt version by ID */
  deletePromptVersion(versionId: string): Observable<void> {
    return this.http
      .delete(`${this.promptManagerUrl}/ui/versions/${versionId}`, {
        headers: this.getHeaders(),
        responseType: "text",
      })
      .pipe(map(() => undefined), catchError(this.handleError));
  }

  /** Returns all registered keys for a service. */
  getKeys(service: string): Observable<PromptKeyItem[]> {
    return this.http
      .get<PromptKeyItem[]>(`${this.promptManagerUrl}/ui/services/${service}/keys`)
      .pipe(catchError(this.handleError));
  }

  /** Updates a prompt version (template and inject workflow only). */
  updatePromptVersion(
    versionId: string,
    payload: UpdatePromptVersionPayload
  ): Observable<void> {
    return this.put<void>(
      `${this.promptManagerUrl}/ui/versions/${versionId}`,
      payload
    ).pipe(catchError(this.handleError));
  }

  /** Creates a new prompt. */
  createPrompt(payload: CreatePromptPayload): Observable<void> {
    return this.post<void>(`${this.promptManagerUrl}/ui/prompts`, payload);
  }

  /** Sets the active version for a prompt. */
  setActiveVersion(
    promptId: string,
    payload: SetActiveVersionPayload
  ): Observable<void> {
    return this.http
      .patch<void>(
        `${this.promptManagerUrl}/ui/prompts/${promptId}/active-version`,
        payload,
        {
          headers: this.getHeaders(),
        }
      )
      .pipe(catchError(this.handleError));
  }


  importExistingData(projectId: string, suiteId: string, file: File, caseIds?: any) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('projectId', projectId);
    formData.append('testSuiteId', suiteId);
    formData.append(
      'testCaseIds',
      Array.isArray(caseIds) ? JSON.stringify(caseIds) : caseIds ?? ''
    );

    return this.postFormData<any>(`${this.tdgUrl}/custom-table-excel`, formData);
  }
}
